import {
  useCallback,
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { VerticalMoveDirection } from '../../../../../domain/runner/runnerTypes';
import { FLIGHT_THRUST_KEY } from '../gameplayConstants';

export type PlayfieldPointerHandlers = {
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
};

export function useHoldFlightInput(options?: {
  enabled?: boolean;
  onThrustStart?: () => void;
}): {
  getVerticalDirection: () => VerticalMoveDirection;
  playfieldPointerHandlers: PlayfieldPointerHandlers;
} {
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onThrustStartRef = useRef(options?.onThrustStart);
  onThrustStartRef.current = options?.onThrustStart;

  const spaceHeldRef = useRef(false);
  const pointerHeldRef = useRef(false);

  const notifyThrustStart = useCallback((wasHeld: boolean) => {
    if (!wasHeld) {
      onThrustStartRef.current?.();
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!enabledRef.current || event.code !== FLIGHT_THRUST_KEY) {
        return;
      }
      event.preventDefault();
      const wasHeld = spaceHeldRef.current || pointerHeldRef.current;
      spaceHeldRef.current = true;
      notifyThrustStart(wasHeld);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== FLIGHT_THRUST_KEY) {
        return;
      }
      spaceHeldRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [notifyThrustStart]);

  useEffect(() => {
    if (!enabled) {
      spaceHeldRef.current = false;
      pointerHeldRef.current = false;
    }
  }, [enabled]);

  const getVerticalDirection = useCallback((): VerticalMoveDirection => {
    if (!enabledRef.current) {
      return 0;
    }
    if (spaceHeldRef.current || pointerHeldRef.current) {
      return 1;
    }
    return -1;
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabledRef.current) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    const wasHeld = spaceHeldRef.current || pointerHeldRef.current;
    pointerHeldRef.current = true;
    notifyThrustStart(wasHeld);
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [notifyThrustStart]);

  const releasePointer = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointerHeldRef.current = false;
  }, []);

  return {
    getVerticalDirection,
    playfieldPointerHandlers: {
      onPointerDown,
      onPointerUp: releasePointer,
      onPointerCancel: releasePointer,
    },
  };
}
